/**
 * The background-scroll lock, reference counted, shared by every surface that
 * covers the page: Modal, the full-screen photo viewers (useOverlayKeyboard),
 * the date pickers, the map and memory overlays, the tour spotlight, the trip
 * review form.
 *
 * A count rather than each surface restoring the value it captured on the way
 * in, because those captures interleave and the interleaving is not fixable
 * locally. React 19 destroys a deleted subtree PARENT-FIRST, so when a viewer
 * opened from inside a Modal is torn down with it (a route change; Android
 * Back out of the recap composer) the Modal's cleanup runs FIRST. Whatever the
 * Modal does, the viewer's cleanup runs afterwards and writes back the
 * 'hidden' it captured when it opened over that still-locked Modal — leaving
 * the whole app unscrollable with nothing mounted to own it. Guarding the
 * restore on "is it still 'hidden'?" only moves the problem: it made the
 * viewer's correctness depend on the Modal having already written '', which is
 * not something either of them can promise about the other.
 *
 * The other failure it ends is the mirror image, and it was just as live: a
 * surface whose cleanup wrote '' unconditionally unlocked the page behind
 * whatever was still open — the tour's endTour did it to any modal on screen,
 * and LocationMapPopup did it even when it had never opened.
 *
 * With a count, no surface has to know the others exist. Whoever is last out
 * releases, in any teardown order, and a surface that opens over an
 * already-locked page simply does not change it.
 *
 * It writes '' on release rather than a captured value. Every caller wants the
 * same thing — "stop covering the page" — and restoring a capture would
 * reintroduce the interleaving for anything that ever writes body overflow
 * outside this count.
 *
 * ## Using it
 *
 *     useEffect(() => {
 *       if (!isOpen) return undefined;
 *       return lockBodyScroll();          // the release IS the cleanup
 *     }, [isOpen]);
 *
 * Acquire when the surface appears, call the returned release exactly once
 * when it goes. Releasing twice is a no-op, so an effect cleanup that runs
 * again after React has already torn it down cannot drive the count negative —
 * but acquiring without releasing leaks the page into a locked state, so the
 * release must be reachable from every path that closes the surface. Where the
 * surface is opened and closed by CALLBACKS rather than by an effect (the tour
 * spotlight), hold the release in a ref and call it in the closing callback.
 */
let scrollLockCount = 0;

export function lockBodyScroll() {
  scrollLockCount += 1;
  document.body.style.overflow = 'hidden';
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) document.body.style.overflow = '';
  };
}

/** Test seam: how many surfaces currently hold the lock. */
export function bodyScrollLockCount() {
  return scrollLockCount;
}
